import { useEffect, useState } from 'react';
import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
    keepPreviousData
} from '@tanstack/react-query';
import {
    createOrthodontist,
    fetchOrthodontists,
    type CreateOrthodontistBody,
    type OrthodontistsListParams
} from '../services/orthodontistAccounts';
import {
    ACCOUNTS_ORTHODONTISTS_INFINITE_KEY,
    ACCOUNTS_ORTHODONTISTS_KEY,
    ACCOUNTS_ORTHODONTISTS_LIST_KEY
} from './queryKeys';

const EMPTY_ORTHODONTISTS_META = { total: 0, page: 1, limit: 0, totalPages: 1 };

export function useOrthodontists(params: OrthodontistsListParams & { enabled?: boolean }) {
    const { enabled = true, ...rest } = params;
    const queryKey = [
        ACCOUNTS_ORTHODONTISTS_KEY,
        'list',
        rest.page,
        rest.limit,
        rest.sort ?? '',
        rest.order ?? '',
        rest.q ?? '',
        rest.state ?? ''
    ];

    const { data, isLoading, isFetching, isError, refetch } = useQuery({
        queryKey,
        queryFn: () => fetchOrthodontists(rest),
        staleTime: 60_000,
        placeholderData: keepPreviousData,
        enabled
    });

    return {
        orthodontists: data?.data ?? [],
        meta: data?.meta ?? { ...EMPTY_ORTHODONTISTS_META, page: rest.page, limit: rest.limit },
        isLoading,
        isFetching,
        isError,
        refetch
    };
}

/** Same endpoint, accumulated instead of sliced - `DataTable`'s `'infinite'` mode. */
export function useInfiniteOrthodontists(
    params: Omit<OrthodontistsListParams, 'page'> & { enabled?: boolean }
) {
    const { enabled = true, ...rest } = params;
    const { data, isLoading, isFetching, isFetchingNextPage, isError, hasNextPage, fetchNextPage } =
        useInfiniteQuery({
            queryKey: [
                ACCOUNTS_ORTHODONTISTS_KEY,
                'infinite',
                rest.limit,
                rest.sort ?? '',
                rest.order ?? '',
                rest.q ?? '',
                rest.state ?? ''
            ],
            initialPageParam: 1,
            queryFn: ({ pageParam }) => fetchOrthodontists({ ...rest, page: pageParam as number }),
            getNextPageParam: (lastPage) =>
                lastPage.meta.page * lastPage.meta.limit < lastPage.meta.total
                    ? lastPage.meta.page + 1
                    : undefined,
            staleTime: 60_000,
            placeholderData: keepPreviousData,
            enabled
        });

    const firstPageMeta = data?.pages[0]?.meta;

    return {
        orthodontists: data?.pages.flatMap((page) => page.data) ?? [],
        meta: firstPageMeta ?? { ...EMPTY_ORTHODONTISTS_META, limit: rest.limit },
        isLoading,
        isFetching,
        isError,
        isFetchingNextPage,
        hasNextPage: hasNextPage ?? false,
        fetchNextPage
    };
}

const ORG_SEARCH_DEBOUNCE_MS = 250;
const ORG_SEARCH_RESULT_LIMIT = 6;

// Debounced org-name search backing OrthodontistMultiSelect's typeahead -
// mirrors usePracticeSearch's practice equivalent.
export function useOrthodontistSearch(search: string, enabled: boolean) {
    const [debouncedSearch, setDebouncedSearch] = useState(search);

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(search.trim()), ORG_SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [search]);

    const { data, isLoading } = useQuery({
        queryKey: [ACCOUNTS_ORTHODONTISTS_KEY, 'search', debouncedSearch],
        queryFn: () =>
            fetchOrthodontists({
                page: 1,
                limit: ORG_SEARCH_RESULT_LIMIT,
                q: debouncedSearch || undefined,
                sort: 'name',
                order: 'asc'
            }),
        enabled,
        staleTime: 30_000
    });

    return { results: data?.data ?? [], isLoading };
}

// Create-only counterpart to the list hooks above - see
// OrthodontistCreatePage/useOrthodontistCreateForm for the form that calls it.
export function useCreateOrthodontist() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (body: CreateOrthodontistBody) => createOrthodontist(body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ACCOUNTS_ORTHODONTISTS_LIST_KEY });
            queryClient.invalidateQueries({ queryKey: ACCOUNTS_ORTHODONTISTS_INFINITE_KEY });
        }
    });
}
